import React, { useEffect, useState } from 'react';

interface ExpiringBatch {
  id: number;
  amount_remaining: string;
  expires_at: string;
}

interface BalanceWarnings {
  total_credits: string | null;
  is_unlimited: boolean;
  show_low_balance: boolean;
  expiring_batches: ExpiringBatch[];
}

const CreditWarningsBanner: React.FC = () => {
  const [warnings, setWarnings] = useState<BalanceWarnings | null>(null);
  const [dismissedLow, setDismissedLow] = useState(false);
  const [dismissedExpiry, setDismissedExpiry] = useState(false);

  useEffect(() => {
    fetch('/api/user/balance/', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && !data.error) {
          setWarnings(data);
        }
      })
      .catch(() => {});
  }, []);

  if (!warnings || warnings.is_unlimited) {
    return null;
  }

  const soonest = warnings.expiring_batches?.[0];
  const expiryDate = soonest
    ? new Date(soonest.expires_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  return (
    <div className="space-y-2 p-3">
      {!dismissedExpiry && soonest && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p>
            You have a credit bundle with {soonest.amount_remaining} credits left that will expire
            if unused on {expiryDate}.
          </p>
          <button
            type="button"
            onClick={() => setDismissedExpiry(true)}
            className="text-amber-700 hover:text-amber-900"
            aria-label="Dismiss expiry warning"
          >
            ×
          </button>
        </div>
      )}
      {!dismissedLow && warnings.show_low_balance && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
          <p>
            You only have {warnings.total_credits} credits remaining. Would you like to top up with
            more?
          </p>
          <button
            type="button"
            onClick={() => setDismissedLow(true)}
            className="text-orange-700 hover:text-orange-900"
            aria-label="Dismiss low-balance warning"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default CreditWarningsBanner;
